import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReducerStates } from "../../helpers/constants";
import { IFailedRequest } from "../../services/login.services";
import {
  dashboardServices,
  IBillingManagerData,
} from "../../services/dashboard.services";
import { initialize } from "../app/asyncThunkActions";
import store from "../../helpers/store";
import {
  IInvoiceData,
  INewInvoiceData,
  ISubscriptionData,
} from "../../services/dashboard.services";
import { logout } from "../login/loginSlice";
import { StorageKeys } from "../../services/constants";

export interface ILoginState {
  invoiceLoaded: boolean;
  state: ReducerStates;
  errorCode: string;
  errorMessage: string;
  mode: string;
  authParams: string;
  origin?: string;
  parentClientID?: string;
  subscriptionData: ISubscriptionData[];
  invoiceData: INewInvoiceData[];
  pendingUpdateData: IInvoiceData[];
  loginEmailSent: boolean;
  invoiceSubmitted: boolean;
  selectedSubscription?: ISubscriptionData;
  billingManagers: IBillingManagerData[];
}

export const initialDashboardState: ILoginState = {
  invoiceLoaded: false,
  state: ReducerStates.IDLE,
  errorCode: "",
  errorMessage: "",
  mode: "",
  authParams: "",
  origin: undefined,
  parentClientID: undefined,
  subscriptionData: [],
  invoiceData: [],
  pendingUpdateData: [],
  loginEmailSent: false,
  invoiceSubmitted: false,
  selectedSubscription: undefined,
  billingManagers: [],
};

export const LoginModes = {
  ALEXA: "alexa",
  LOGIN: "login",
};

export const getClientID = () => {
  const parentClientID = store.getState().loginState.parentClientID;
  if (parentClientID !== undefined) {
    return parentClientID;
  } else {
    return (process.env.REACT_APP_CLIENT_ID as unknown) as string;
  }
};

// Get current subscription info
export const getSubscriptionInfo = createAsyncThunk(
  "stripe/getSubscriptionInfo",
  async (email: string) => {
    const subscriptionData = await dashboardServices.getSubscriptionInfo(email);
    return subscriptionData;
  }
);

// Get upcoming invoice
export const getUpcomingInvoice = createAsyncThunk(
  "stripe/getUpcomingInvoice",
  async (invoice: IInvoiceData[]) => {
    const subscriptionData = await dashboardServices.getUpcomingInvoice(
      invoice
    );
    return subscriptionData;
  }
);

// Submit invoice
export const submitInvoice = createAsyncThunk(
  "stripe/submitInvoice",
  async (invoice: IInvoiceData[]) => {
    const subscriptionData = await dashboardServices.submitInvoice(invoice);
    return subscriptionData;
  }
);

// Get user's billing manager info
export const getBillingManagers = createAsyncThunk(
  "stripe/getBillingManagers",
  async () => {
    const billingManagerData = await dashboardServices.getBillingManagers();
    return billingManagerData;
  }
);

// Create a billing manager for a user
export const createBillingManager = createAsyncThunk(
  "stripe/createBillingManager",
  async (email: string) => {
    const billingManagerData = await dashboardServices.createBillingManager(
      email
    );
    return billingManagerData;
  }
);

// Delete a user's billing manager by email
export const deleteBillingManager = createAsyncThunk(
  "stripe/deleteBillingManager",
  async (email: string, { rejectWithValue }) => {
    try {
      await dashboardServices.deleteBillingManager(email);
      return email;
    } catch (err) {
      return rejectWithValue((err as unknown) as IFailedRequest);
    }
  }
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: initialDashboardState,
  reducers: {
    clearSession(state) {
      state.subscriptionData = [];
      state.invoiceData = [];
      state.pendingUpdateData = [];
      state.billingManagers = [];
      state.loginEmailSent = false;
      state.invoiceSubmitted = false;
      state.selectedSubscription = undefined;
      state.loginEmailSent = false;
      localStorage.removeItem(StorageKeys.USER);
      localStorage.removeItem(StorageKeys.SUBSCRIPTION_DATA);
      localStorage.removeItem(StorageKeys.SELECTED_SUBSCRIPTION);
    },
    subscriptionManagementInit(state) {
      state.state = ReducerStates.IDLE;
      state.errorCode = initialDashboardState.errorCode;
      state.errorMessage = initialDashboardState.errorMessage;
    },
    resetSliceState(state) {
      state.state = ReducerStates.IDLE;
    },
    authorizeMode(state, action) {
      state.mode = action.payload.mode;
      state.authParams = action.payload.authParams;
    },
    setFrameOrigin(state, action: PayloadAction<string>) {
      state.origin = action.payload;
    },
    resetInvoice(state) {
      state.invoiceSubmitted = false;
      state.invoiceLoaded = false;
      state.invoiceData = [];
    },
    resetSubscription(state) {
      state.selectedSubscription = undefined;
      localStorage.removeItem(StorageKeys.SELECTED_SUBSCRIPTION);
    },
    setPendingUpdateData(state, action: PayloadAction<IInvoiceData[]>) {
      state.pendingUpdateData = action.payload;
    },
    setSelectedSubscription(state, action: PayloadAction<ISubscriptionData>) {
      state.selectedSubscription = action.payload;
      localStorage.setItem(
        StorageKeys.SELECTED_SUBSCRIPTION,
        JSON.stringify(action.payload)
      );
    },
    clearErrors(state) {
      state.errorMessage = "";
      state.errorCode = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSubscriptionInfo.pending, (state) => {
        state.state = ReducerStates.PENDING;
        state.loginEmailSent = false;
        state.invoiceLoaded = false;
        state.errorCode = initialDashboardState.errorCode;
        state.errorMessage = initialDashboardState.errorMessage;
      })
      .addCase(getSubscriptionInfo.fulfilled, (state, action) => {
        state.subscriptionData = action.payload;
        state.state = ReducerStates.SUCCEEDED;
        localStorage.setItem(
          StorageKeys.SUBSCRIPTION_DATA,
          JSON.stringify(action.payload)
        );
      })
      .addCase(getSubscriptionInfo.rejected, (state, action) => {
        const error = (action.error as unknown) as IFailedRequest;
        state.errorCode = error?.code || "";
        state.errorMessage = error?.message || "";
        state.state = ReducerStates.FAILED;
      })
      .addCase(getBillingManagers.pending, (state) => {
        state.state = ReducerStates.PENDING;
        state.errorCode = initialDashboardState.errorCode;
        state.errorMessage = initialDashboardState.errorMessage;
      })
      .addCase(getBillingManagers.fulfilled, (state, action) => {
        state.billingManagers = action.payload;
        state.state = ReducerStates.SUCCEEDED;
      })
      .addCase(getBillingManagers.rejected, (state, action) => {
        const error = (action.error as unknown) as IFailedRequest;
        state.errorCode = error?.code || "";
        state.errorMessage = error?.message || "";
        state.state = ReducerStates.FAILED;
      })
      .addCase(createBillingManager.pending, (state) => {
        state.state = ReducerStates.PENDING;
        state.errorCode = initialDashboardState.errorCode;
        state.errorMessage = initialDashboardState.errorMessage;
      })
      .addCase(createBillingManager.fulfilled, (state, action) => {
        state.billingManagers = action.payload;
        state.state = ReducerStates.SUCCEEDED;
      })
      .addCase(createBillingManager.rejected, (state, action) => {
        const error = (action.error as unknown) as IFailedRequest;
        state.errorCode = error?.code || "";
        state.errorMessage = error?.message || "";
        state.state = ReducerStates.FAILED;
      })
      .addCase(deleteBillingManager.pending, (state) => {
        state.state = ReducerStates.PENDING;
        state.errorCode = initialDashboardState.errorCode;
        state.errorMessage = initialDashboardState.errorMessage;
      })
      .addCase(deleteBillingManager.fulfilled, (state, action) => {
        state.billingManagers = state.billingManagers.filter(
          (manager) => manager.user_email !== action.payload
        );
        state.state = ReducerStates.SUCCEEDED;
      })
      .addCase(deleteBillingManager.rejected, (state, action) => {
        const error = (action.error as unknown) as IFailedRequest;
        state.errorCode = error?.code || "";
        state.errorMessage = error?.message || "";
        state.state = ReducerStates.FAILED;
      })
      .addCase(getUpcomingInvoice.pending, (state) => {
        state.state = ReducerStates.PENDING;
        state.errorCode = initialDashboardState.errorCode;
        state.errorMessage = initialDashboardState.errorMessage;
      })
      .addCase(getUpcomingInvoice.fulfilled, (state, action) => {
        state.invoiceLoaded = true;
        state.invoiceData = action.payload;
        state.state = ReducerStates.SUCCEEDED;
      })
      .addCase(getUpcomingInvoice.rejected, (state, action) => {
        const error = (action.error as unknown) as IFailedRequest;
        state.errorCode = error?.code || "";
        state.errorMessage = error?.message || "";
        state.state = ReducerStates.FAILED;
      })
      .addCase(submitInvoice.pending, (state) => {
        state.state = ReducerStates.PENDING;
        state.errorCode = initialDashboardState.errorCode;
        state.errorMessage = initialDashboardState.errorMessage;
      })
      .addCase(submitInvoice.fulfilled, (state, action) => {
        state.invoiceSubmitted = true;
        state.invoiceLoaded = false;
        state.invoiceData = [];
        state.pendingUpdateData = [];
        const _subData = localStorage.getItem(StorageKeys.SUBSCRIPTION_DATA);
        const subData =
          _subData !== null
            ? (JSON.parse(_subData) as ISubscriptionData[])
            : _subData;

        if (subData) {
          action.payload.forEach((subscription) => {
            const subIndex = subData.findIndex(
              (sub) => sub.subscription_id === subscription.subscription_id
            );
            if (subIndex !== -1) {
              subData[subIndex] = subscription;
            }
            localStorage.setItem(
              StorageKeys.SUBSCRIPTION_DATA,
              JSON.stringify(subData)
            );
          });
          state.subscriptionData = subData;
        } else {
          localStorage.setItem(
            StorageKeys.SUBSCRIPTION_DATA,
            JSON.stringify(action.payload)
          );
          state.subscriptionData = action.payload;
        }

        state.state = ReducerStates.SUCCEEDED;
      })
      .addCase(submitInvoice.rejected, (state, action) => {
        const error = (action.error as unknown) as IFailedRequest;
        state.errorCode = error?.code || "";
        state.errorMessage = error?.message || "";
        state.state = ReducerStates.FAILED;
      })
      .addCase(initialize.fulfilled, (state, action) => {
        if (action.payload.subData !== null) {
          state.subscriptionData = action.payload.subData;
        } else {
          state.subscriptionData = [];
        }
        if (action.payload.selectedSubData !== null) {
          state.selectedSubscription = action.payload.selectedSubData;
        } else {
          state.selectedSubscription = undefined;
        }
        state.loginEmailSent = false;
        state.invoiceLoaded = false;
        state.invoiceData = [];
        state.pendingUpdateData = [];
        state.billingManagers = [];
      })
      .addCase(logout.type, (state) => {
        return initialDashboardState;
      });
  },
});

const { reducer } = dashboardSlice;

// Destructure and export the plain action creators
export const {
  resetSliceState,
  authorizeMode,
  clearSession,
  subscriptionManagementInit,
  resetInvoice,
  resetSubscription,
  setPendingUpdateData,
  setSelectedSubscription,
  clearErrors,
} = dashboardSlice.actions;

export default reducer;
